/* runs.js — derived cabinet-RUN intelligence (v0.53). DERIVED, NEVER PERSISTED.
 *
 * Professional CAD (Cabinet Vision / 2020 / Microvellum) treats a run — a chain of
 * flush cabinets, possibly turning corners — as a first-class object: it is what
 * fillers, end panels, worktop joins and toe-kick continuity are computed against.
 * This module derives that object from the EXISTING placement model ({wallId,
 * absLeft, config}) + the wall topology graph. Nothing is written back: no schema
 * change, no placement change, no UI change. Consumers ask; the layer answers.
 *
 * LINE MODEL (matches how pros think about elevations):
 *   - BASE line  — floor-standing chain: cabinet-base + cabinet-tall + appliances
 *     (a dishwasher is a base-run member even though its collision band is 0).
 *     Worktop, plinth/toe-kick and base fillers belong to this line.
 *   - UPPER line — wall-hung chain: cabinet-wall + cabinet-tall (a tall unit is a
 *     member of BOTH lines: it terminates the worktop but continues the elevation).
 *     Cornice and upper fillers belong to this line.
 *   - stack / bridge levels are not line-classified yet (runId stays null) — a
 *     future iteration adds them as their own lines.
 *
 * Semantics (documented against existing engine conventions):
 *   - "left"/"right" = toward wall start / toward wall end (absLeft order — the
 *     same convention snapSides uses).
 *   - Same-wall, same-line chaining when the edge gap ∈ [−RULE_EPS, RUN_GAP_TOL]:
 *     the validator's 2 mm AABB grace counts as flush; a true overlap does not chain.
 *   - Corner continuity: at a graph node (corner / continuation / tee / cross) the
 *     line's terminal cabinet on one incident wall links to the terminal on another
 *     when BOTH sit within RUN_CORNER_REACH_M of the joint — the same 1.0 m "run
 *     meets this corner" reach maybeAutoCorner has always used (~32451). The
 *     across-joint partner is exposed as cornerNeighbour (distinct from left/right),
 *     and the transition records the node kind + kitchen corner classification.
 *   - Islands and wall-less legacy placements do not participate (runId null).
 *   - A tall unit belongs to two runs; its byPlacement record reports the BASE-line
 *     run as primary (CV convention). Both memberships are visible in runs[].
 *
 * Run ids are DETERMINISTIC for a given state (run_<line>_<first member id>,
 * members ordered by wall order then absLeft) but are NOT stable across edits —
 * they are a derived view, not identity. Do not persist them.
 *
 * Cost: O(P log P + n²) — sub-millisecond at the 100-wall / 1000-cabinet target.
 * Callers doing per-frame work should build once per interaction, like
 * buildWallGraph (getWallGraph memoizes the topology part).
 */
import { getWallGraph } from "./wallGraph";
import { effDef } from "./pricing";
import { RULE_EPS, LAYER_BANDS } from "./constants";

export const RUN_GAP_TOL = 0.005;        // 5 mm — flush-chain gap ceiling (WALL_EPS family)
export const RUN_CORNER_REACH_M = 1.0;   // maybeAutoCorner's "run meets corner" reach (~32451)

const LINES = ["base", "upper"];
/** Which lines a role belongs to (see LINE MODEL above). */
export function linesOfRole(role) {
  const band = LAYER_BANDS[role] || 0;
  const out = [];
  if ((band & 1) !== 0 || role === "appliance") out.push("base");
  if ((band & 2) !== 0) out.push("upper");
  return out;
}

/** buildRunGraph(placements, lib, walls) →
 *  { runs: [{id, line, members, wallIds, transitions, totalWidthM}], byPlacement: Map }.
 *  byPlacement entry: { runId, leftNeighbour, rightNeighbour, cornerNeighbour,
 *  indexInRun } — null/-1 for non-participants (islands, wall-less, unknown SKU,
 *  unclassified levels). Neighbour fields describe the placement's PRIMARY line. */
export function buildRunGraph(placements, lib, walls) {
  const graph = getWallGraph(walls);
  const all = placements || [];
  const byPlacement = new Map();
  all.forEach((p) => byPlacement.set(p.id, {
    runId: null, leftNeighbour: null, rightNeighbour: null, cornerNeighbour: null, indexInRun: -1,
  }));

  // Eligible members: on a real wall, not an island, resolvable definition, ≥1 line.
  const members = [];
  all.forEach((p) => {
    if (!p.wallId || p.island) return;
    const def = effDef(p, lib);
    if (!def || !graph.walls.get(p.wallId)) return;
    const lines = linesOfRole(def.role);
    if (!lines.length) return;
    const left = p.absLeft || 0;
    members.push({ p, def, lines, left, right: left + (def.width || 0) });
  });

  const wallOrder = new Map();
  (walls || []).forEach((w, i) => wallOrder.set(w.id, i));
  const runs = [];

  // ── One independent pass per LINE: chain → corner-link → components → records.
  LINES.forEach((line) => {
    const lineIdx = members.map((m, i) => i).filter((i) => members[i].lines.includes(line));
    if (!lineIdx.length) return;
    const primary = (m) => m.lines[0] === line; // base wins for dual-line members (talls)

    const parent = new Map(lineIdx.map((i) => [i, i]));
    const find = (i) => { let r = i; while (parent.get(r) !== r) r = parent.get(r); parent.set(i, r); return r; };
    const union = (i, j) => { const a = find(i), b = find(j); if (a !== b) parent.set(b, a); };

    /* 1. Same-wall flush chaining + left/right neighbours (primary-line records only) */
    const byWall = new Map();
    lineIdx.forEach((i) => {
      const wid = members[i].p.wallId;
      if (!byWall.has(wid)) byWall.set(wid, []);
      byWall.get(wid).push(i);
    });
    byWall.forEach((idxs) => {
      idxs.sort((a, b) => members[a].left - members[b].left);
      for (let x = 0; x < idxs.length; x++) {
        for (let y = x + 1; y < idxs.length; y++) {
          const a = members[idxs[x]], b = members[idxs[y]];
          const gap = b.left - a.right;
          if (gap > RUN_GAP_TOL) break;   // sorted — nothing later is closer
          if (gap < -RULE_EPS) continue;  // true overlap (invalid state) — not a chain
          union(idxs[x], idxs[y]);
          const A = byPlacement.get(a.p.id), B = byPlacement.get(b.p.id);
          if (primary(a) && A.rightNeighbour == null) A.rightNeighbour = b.p.id;
          if (primary(b) && B.leftNeighbour == null) B.leftNeighbour = a.p.id;
        }
      }
    });

    /* 2. Corner / continuation continuity via graph nodes (this line only) */
    const transitions = [];
    graph.nodes.forEach((node) => {
      if (node.kind === "free") return;
      const cands = [];
      node.ends.forEach((e) => {
        const wi = graph.walls.get(e.wallId);
        if (!wi || !byWall.has(e.wallId)) return;
        let best = null;
        byWall.get(e.wallId).forEach((mi) => {
          const m = members[mi];
          const d = e.end === "start" ? m.left : wi.len - m.right;
          if (d >= 0 && d <= RUN_CORNER_REACH_M && (!best || d < best.d)) best = { mi, d };
        });
        if (best) cands.push(best);
      });
      const pairs = [];
      for (let i = 0; i < cands.length; i++)
        for (let j = i + 1; j < cands.length; j++) {
          const a = members[cands[i].mi], b = members[cands[j].mi];
          if (a.p.wallId === b.p.wallId) continue;
          pairs.push({ i, j, gap: cands[i].d + cands[j].d });
        }
      pairs.sort((u, v) => u.gap - v.gap);
      const used = new Set();
      pairs.forEach(({ i, j, gap }) => {
        if (used.has(i) || used.has(j)) return;
        used.add(i); used.add(j);
        const a = members[cands[i].mi], b = members[cands[j].mi];
        union(cands[i].mi, cands[j].mi);
        if (primary(a)) byPlacement.get(a.p.id).cornerNeighbour = b.p.id;
        if (primary(b)) byPlacement.get(b.p.id).cornerNeighbour = a.p.id;
        transitions.push({ nodeId: node.id, kind: node.kind, cornerKind: node.cornerKind || null, aId: a.p.id, bId: b.p.id, gapM: gap });
      });
    });

    /* 3. Components → deterministic run records for this line */
    const comps = new Map();
    lineIdx.forEach((i) => {
      const r = find(i);
      if (!comps.has(r)) comps.set(r, []);
      comps.get(r).push(i);
    });
    [...comps.values()].forEach((idxs) => {
      idxs.sort((a, b) => {
        const A = members[a], B = members[b];
        const wo = (wallOrder.get(A.p.wallId) || 0) - (wallOrder.get(B.p.wallId) || 0);
        return wo !== 0 ? wo : A.left - B.left;
      });
      const memberIds = idxs.map((i) => members[i].p.id);
      const runId = "run_" + line + "_" + memberIds[0];
      runs.push({
        id: runId,
        line,
        members: memberIds,
        wallIds: [...new Set(idxs.map((i) => members[i].p.wallId))],
        transitions: transitions.filter((t) => memberIds.includes(t.aId) && memberIds.includes(t.bId)),
        totalWidthM: idxs.reduce((s, i) => s + (members[i].def.width || 0), 0),
      });
      idxs.forEach((i, pos) => {
        if (!primary(members[i])) return; // dual-line members report their PRIMARY run
        const rec = byPlacement.get(members[i].p.id);
        rec.runId = runId;
        rec.indexInRun = pos;
      });
    });
  });

  runs.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)); // deterministic output order
  return { runs, byPlacement };
}

/** Convenience: the derived run record for ONE placement (builds the run graph). */
export function runInfoFor(p, placements, lib, walls) {
  if (!p) return null;
  return buildRunGraph(placements, lib, walls).byPlacement.get(p.id) || null;
}
